import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnVisibilityState } from '@tanstack/react-table';

import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import StatusDot from '@/components/atoms/StatusDot';
import PageLayout from '@/components/Layout/PageLayout';
import { DataTable } from '@/components/organisms/DataTable';
import type { CageListItem } from '@/domain/cages';

import { statusDotTone, statusLabel } from './cageStatus';
import CagesFilter from './components/CagesFilter';
import { useCageListColumns } from './useCageListColumns';

import styles from './CagesList.module.scss';

export type CageStatusCounts = Record<CageListItem['status'], number>;

interface CagesListViewProps {
    cages: CageListItem[];
    cageStatusCounts: CageStatusCounts;
    filter: string;
    onFilterChange: (value: string) => void;
    selectedClientId: string;
    onSelectedClientIdChange: (value: string) => void;
    clientOptions: Array<{ value: string; label: string }>;
    selectedLocationId: string;
    onSelectedLocationIdChange: (value: string) => void;
    locationOptions: Array<{ value: string; label: string }>;
    hasActiveFilters: boolean;
    onClearFilters: () => void;
    columnVisibility: ColumnVisibilityState;
    onColumnVisibilityChange: (visibility: ColumnVisibilityState) => void;
    pageSize: number;
    onPageSizeChange: (pageSize: number) => void;
    /** The `fish-group-tracking` flag. Off, the table and its links are as before fish groups. */
    fishGroupsEnabled: boolean;
}

export default function CagesListView(props: CagesListViewProps) {
    const {
        cages,
        cageStatusCounts,
        filter,
        onFilterChange,
        selectedClientId,
        onSelectedClientIdChange,
        clientOptions,
        selectedLocationId,
        onSelectedLocationIdChange,
        locationOptions,
        hasActiveFilters,
        onClearFilters,
        columnVisibility,
        onColumnVisibilityChange,
        pageSize,
        onPageSizeChange,
        fishGroupsEnabled,
    } = props;
    const { t } = useTranslation();
    const cageStatusCountItems = useMemo(
        () =>
            (['active', 'inactive', 'historical'] as const).map((status) => ({
                status,
                count: cageStatusCounts[status],
                label: t(statusLabel(status)),
            })),
        [cageStatusCounts, t]
    );

    const columns = useCageListColumns(fishGroupsEnabled);

    return (
        <PageLayout
            title={t('internal.cages.title')}
            actions={
                <div className={styles.statusCounts}>
                    {cageStatusCountItems.map((item) => (
                        <StatusDot
                            key={item.status}
                            tone={statusDotTone(item.status)}
                            className={styles.statusCountItem}>
                            {item.label}: <strong>{item.count}</strong>
                        </StatusDot>
                    ))}
                </div>
            }>
            <Card as="section" padding="xl">
                <div className={styles.toolbar}>
                    <CagesFilter
                        filter={filter}
                        onFilterChange={onFilterChange}
                        selectedClientId={selectedClientId}
                        onSelectedClientIdChange={onSelectedClientIdChange}
                        clientOptions={clientOptions}
                        selectedLocationId={selectedLocationId}
                        onSelectedLocationIdChange={onSelectedLocationIdChange}
                        locationOptions={locationOptions}
                    />
                    <Button
                        variant="secondary"
                        className={styles.clearFiltersButton}
                        onClick={onClearFilters}
                        disabled={!hasActiveFilters}>
                        {t('internal.cages.clearFilters')}
                    </Button>
                </div>
                <DataTable
                    responsive
                    columns={columns}
                    data={cages}
                    filter={filter}
                    initialSort={[
                        { id: 'status', desc: false },
                        { id: 'lastMeasurement', desc: true },
                    ]}
                    enablePagination
                    pageSize={pageSize}
                    pageSizeOptions={[15, 25, 50, 100, 1000]}
                    onPageSizeChange={onPageSizeChange}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    enableColumnSelector
                />
            </Card>
        </PageLayout>
    );
}
